import { Typography } from '@mui/material';
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UNSAFE_NavigationContext as NavigationContext } from 'react-router-dom';

import Modal from '@/components/common/Modal/Modal';

export type UnsavedChanges = {
    isDirty: boolean;
    canSave: boolean;
    save: () => Promise<boolean>;
};

type RegisterUnsavedChanges = (unsavedChanges?: UnsavedChanges) => void;

const RegisterUnsavedChangesContext = createContext<RegisterUnsavedChanges | null>(null);

/**
 * Asks before leaving a view with unsaved edits, wherever the user clicks - the side menu, the tabs,
 * a breadcrumb or a button on the page. Router 6.2 has no navigation blocker, so this wraps the router's
 * own navigator instead: every `navigate()` and every `<Link>` in the app goes through it.
 *
 * Not covered: the browser's own back button and reload, which never reach the router.
 */
export const UnsavedChangesProvider = ({ children }: { children: ReactNode }) => {
    const { t } = useTranslation();
    const navigation = useContext(NavigationContext);

    const [unsavedChanges, setUnsavedChanges] = useState<UnsavedChanges>();
    const [pending, setPending] = useState<{ proceed: () => void }>();
    const [isSaving, setIsSaving] = useState(false);

    const register = useCallback<RegisterUnsavedChanges>(changes => setUnsavedChanges(changes), []);

    const isDirty = !!unsavedChanges?.isDirty;

    const guardedNavigation = useMemo(() => {
        const { navigator } = navigation;

        // proceed() calls the untouched navigator, so leaving the view cannot ask a second time
        const hold = (proceed: () => void) => (isDirty ? setPending({ proceed }) : proceed());

        return {
            ...navigation,
            navigator: {
                ...navigator,
                push: (...args: Parameters<typeof navigator.push>) => hold(() => navigator.push(...args)),
                replace: (...args: Parameters<typeof navigator.replace>) => hold(() => navigator.replace(...args)),
                go: (delta: number) => hold(() => navigator.go(delta)),
            },
        };
    }, [navigation, isDirty]);

    const leave = () => {
        pending?.proceed();
        setPending(undefined);
    };

    const saveAndLeave = async () => {
        if (!unsavedChanges) return;

        setIsSaving(true);
        const isSaved = await unsavedChanges.save();
        setIsSaving(false);

        if (isSaved) leave();
    };

    return (
        <RegisterUnsavedChangesContext.Provider value={register}>
            <NavigationContext.Provider value={guardedNavigation}>{children}</NavigationContext.Provider>

            <Modal
                open={!!pending}
                onClose={() => setPending(undefined)}
                title={t('common_one_moment')}
                primaryAction={{
                    label: t('editUserDetailsView_saveButton_title'),
                    onClick: saveAndLeave,
                    disabled: !unsavedChanges?.canSave || isSaving,
                }}
                secondaryAction={{ label: t('personalize_request_details_leave_without_saving'), onClick: leave }}
            >
                <Typography variant="body">{t('personalize_request_details_unsaved_changes')}</Typography>
            </Modal>
        </RegisterUnsavedChangesContext.Provider>
    );
};

/** Called by the form that owns the edits. Unregisters itself on unmount. */
export const useRegisterUnsavedChanges = ({ isDirty, canSave, save }: UnsavedChanges) => {
    const register = useContext(RegisterUnsavedChangesContext);
    if (!register) throw new Error('useRegisterUnsavedChanges has to be used inside UnsavedChangesProvider');

    // the prompt has to save the latest values, not the ones from the render that registered them
    const saveRef = useRef(save);
    useEffect(() => {
        saveRef.current = save;
    });

    useEffect(() => {
        register({ isDirty, canSave, save: () => saveRef.current() });

        return () => register(undefined);
    }, [register, isDirty, canSave]);
};
